import { useContext, useEffect, useCallback, useRef, useState } from 'react';
import { PusherContext } from '../providers/PusherProvider';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Hook for listening to AI chat order drafts via Pusher.
 *
 * Subscribes to the user-{userId}-alerts channel and listens for:
 * - ai-order-draft: when the AI chat builds (or rejects/needs-info) an order draft
 *
 * The channel binding is stable (depends only on pusher/userId) — `onDraft` is
 * read via a ref so a frequently-changing callback (e.g. one that depends on
 * live quote state) doesn't cause the Pusher listener to unbind/rebind, which
 * can drop an event that arrives in that gap.
 */
const usePusherAiOrderDraft = (onDraft) => {
  const pusher = useContext(PusherContext);
  const { user } = useContext(AuthContext);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastDraft, setLastDraft] = useState(null);
  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;

  const handleDraft = useCallback((data) => {
    setLastDraft(data);
    if (typeof onDraftRef.current === 'function') {
      onDraftRef.current(data);
    }
  }, []);

  useEffect(() => {
    if (!pusher || !user?.id) {
      setIsSubscribed(false);
      return;
    }

    const channelName = `user-${user.id}-alerts`;
    let channel = pusher.channel(channelName);
    if (!channel) {
      channel = pusher.subscribe(channelName);
      channel.bind('pusher:subscription_error', (status) => {
        console.error(`[usePusherAiOrderDraft] Subscription error for ${channelName}:`, status);
      });
    }

    channel.bind('ai-order-draft', handleDraft);
    setIsSubscribed(true);

    return () => {
      if (channel) {
        channel.unbind('ai-order-draft', handleDraft);
      }
    };
  }, [pusher, user?.id, handleDraft]);

  return { isSubscribed, lastDraft };
};

export default usePusherAiOrderDraft;
